import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioRecorder } from "@shared/hooks/useAudioRecorder";
import { useAudioPlayer } from "@shared/hooks/useAudioPlayer";

export type CallState =
    | "idle"
    | "connecting"
    | "listening"
    | "thinking"
    | "speaking"
    | "escalating"   // Fenna is paging a human; audio still with Fenna
    | "bridged"      // Human operator is now on the line
    | "ended"
    | "error";

export interface TranscriptTurn {
    who: "caller" | "fenna";
    text: string;
    t: number;
}

export interface KioskContext {
    siteId: string;
    location?: string;
    machine?: string;
}

export interface UseParkingCallResult {
    state: CallState;
    error: string | null;
    lastToolCall: string | null;
    transcript: TranscriptTurn[];
    operatorName: string | null;
    start: () => Promise<void>;
    stop: () => Promise<void>;
}

/**
 * useParkingCall wires the mic → WS → speaker chain for the parking kiosk.
 *
 * WS endpoint is relative — the local proxy (`backend/kiosk_proxy.py`) or the
 * production Container App front-door serves both the static SPA and the WS,
 * so the browser can talk to it without CORS / same-origin issues.
 *
 * The proxy adds the Foundry `Authorization: Bearer` header the browser can't set.
 */
export function useParkingCall(ctx: KioskContext): UseParkingCallResult {
    const [state, setState] = useState<CallState>("idle");
    const [error, setError] = useState<string | null>(null);
    const [lastToolCall, setLastToolCall] = useState<string | null>(null);
    const [transcript, setTranscript] = useState<TranscriptTurn[]>([]);
    const [operatorName, setOperatorName] = useState<string | null>(null);

    const wsRef = useRef<WebSocket | null>(null);

    // Stable mic callback so useAudioRecorder isn't re-created every render.
    const handleAudioInRef = useRef<(chunk: ArrayBuffer) => void>(() => {});
    handleAudioInRef.current = (chunk: ArrayBuffer) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(chunk);
        }
    };
    const stableAudioIn = useCallback((chunk: ArrayBuffer) => {
        handleAudioInRef.current(chunk);
    }, []);

    const player = useAudioPlayer(24000);
    const recorder = useAudioRecorder(stableAudioIn);

    // Refs so cleanup can grab the latest instance without re-creating callbacks.
    const recorderRef = useRef(recorder);
    const playerRef = useRef(player);
    recorderRef.current = recorder;
    playerRef.current = player;

    const teardown = useCallback(async () => {
        try { await recorderRef.current.stop(); } catch { /* noop */ }
        try { await playerRef.current.cleanup(); } catch { /* noop */ }
        if (wsRef.current) {
            try { wsRef.current.close(); } catch { /* noop */ }
            wsRef.current = null;
        }
    }, []);

    const start = useCallback(async () => {
        // Idempotent: extra button presses while a session is live are ignored.
        if (wsRef.current) return;
        setError(null);
        setLastToolCall(null);
        setTranscript([]);
        setState("connecting");

        // Ask for the mic FIRST, inside the click's user-gesture window. If we
        // wait until ws.onopen Chrome silently blocks the prompt on some setups
        // (esp. DevTools device emulation) and getUserMedia rejects immediately.
        try {
            await recorderRef.current.start();
        } catch (e) {
            const err = e as DOMException;
            const name = err?.name || "Error";
            const msg = err?.message || String(e);
            let hint = "";
            if (name === "NotAllowedError") {
                hint = " (sta microfoon toe in de site-instellingen van je browser)";
            } else if (name === "NotFoundError") {
                hint = " (geen microfoon gevonden)";
            } else if (name === "NotReadableError") {
                hint = " (microfoon in gebruik door andere app)";
            }
            setError(`Microfoontoegang geweigerd [${name}]: ${msg}${hint}`);
            setState("error");
            return;
        }

        const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
        const url = `${scheme}//${window.location.host}/ws/kiosk?site=${encodeURIComponent(ctx.siteId)}`;
        const ws = new WebSocket(url);
        ws.binaryType = "arraybuffer";
        wsRef.current = ws;

        ws.onopen = () => {
            // Push kiosk context so Fenna greets with the location and skips
            // her generic opener. Falls through to VAD-driven interaction.
            const bits: string[] = [];
            if (ctx.location) bits.push(`locatie "${ctx.location}"`);
            if (ctx.machine) bits.push(`paal ${ctx.machine}`);
            bits.push(`site ${ctx.siteId}`);
            const content =
                `Kiosk-context: de beller staat bij ${bits.join(", ")}. ` +
                `Begroet in ÉÉN korte Nederlandse zin, benoem de locatie, ` +
                `en vraag hoe je kan helpen. Daarna telkens maximaal één zin.`;
            ws.send(JSON.stringify({ type: "context", content }));
            setState("listening");
        };

        ws.onmessage = async (ev) => {
            if (ev.data instanceof ArrayBuffer) {
                // 8-byte protocol header + PCM16 payload (invocations_ws v2.0.0).
                const buf: ArrayBuffer = ev.data.byteLength > 8 ? ev.data.slice(8) : ev.data;
                setState("speaking");
                await playerRef.current.play(buf);
                return;
            }
            try {
                const evt = JSON.parse(ev.data);
                switch (evt.type) {
                    case "session_started":
                        break;
                    case "user_speech_started":
                        // Barge-in: drop any queued Fenna audio so the user isn't talked over.
                        playerRef.current.stop();
                        setState("listening");
                        break;
                    case "user_speech_stopped":
                        break;
                    case "transcription":
                        if (evt.text) {
                            setTranscript((t) => [...t, { who: "caller", text: evt.text, t: Date.now() }]);
                        }
                        break;
                    case "bot_text":
                        if (evt.final && evt.text) {
                            setTranscript((t) => [...t, { who: "fenna", text: evt.text, t: Date.now() }]);
                        }
                        break;
                    case "tool_call":
                    case "function_call":
                        setLastToolCall(evt.name || evt.tool || "…");
                        setState((s) => (s === "bridged" ? s : "thinking"));
                        break;
                    case "operator_transfer":
                        // Fenna kicked off escalation; a human is being paged.
                        setState((s) => (s === "bridged" ? s : "escalating"));
                        break;
                    case "operator_joined":
                        setOperatorName(evt.operator_name || "medewerker");
                        playerRef.current.stop();  // clear any Fenna audio queue
                        setState("bridged");
                        setLastToolCall(null);
                        break;
                    case "call_ended":
                        setState("ended");
                        void teardown();
                        break;
                    case "response_done":
                        setState((s) => (s === "error" || s === "bridged" || s === "escalating" ? s : "listening"));
                        break;
                    case "error":
                        setError(evt.message || "server error");
                        setState("error");
                        break;
                }
            } catch { /* non-JSON text, ignore */ }
        };

        ws.onerror = () => {
            setError("Verbinding verbroken");
            setState("error");
        };

        ws.onclose = () => {
            setState((s) => (s === "error" ? s : "ended"));
            wsRef.current = null;
            void recorderRef.current.stop().catch(() => {});
        };
    }, [ctx.siteId, ctx.location, ctx.machine, teardown]);

    const stop = useCallback(async () => {
        setState("ended");
        await teardown();
    }, [teardown]);

    // Only tear down on real unmount — never on re-render.
    useEffect(() => {
        return () => { void teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { state, error, lastToolCall, transcript, operatorName, start, stop };
}
